import { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { IconMenu2 } from "@tabler/icons-react";
import { Sidebar } from "../../components/affiliate/Sidebar";
import { InvitationNotifications } from "../../components/affiliate/InvitationNotifications";
import { JoinDiscordModal } from "../../components/affiliate/JoinDiscordModal";
import { AffiliateProvider } from "../../contexts/affiliate";
import { PortalScopeProvider } from "../../contexts/portalScope";
import { PortalBrandingHead } from "@/components/common/PortalBrandingHead";
import { PortalBrandingTheme } from "@/components/common/PortalBrandingTheme";
import OnboardingFloatingMenu from "@/pages/affiliate/onboarding/Tour/OnboardingFloatingMenu";
import { PoweredByReveshare } from "@/components/common/PoweredByReveshare";
import { ScopedMembershipGate } from "@/components/auth/ScopedMembershipGate";

/** Same shell as the brand side: a sticky sidebar and a scrolling main
    column, no top bar. Pages only contribute their own vertical rhythm. */
export function AffiliateLayout() {
  const location = useLocation();
  const [showDiscordModal, setShowDiscordModal] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("affiliateShowDiscordModal") === "true") {
      sessionStorage.removeItem("affiliateShowDiscordModal");
      setShowDiscordModal(true);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isMobileSidebarOpen) setIsMobileSidebarOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobileSidebarOpen]);

  const handleCloseDiscordModal = () => {
    sessionStorage.removeItem("affiliateShowDiscordModal");
    setShowDiscordModal(false);
  };

  // Rewards owns its whole canvas: no gutter, no max width.
  const isFullBleed = location.pathname === "/affiliate/rewards";

  return (
    <AffiliateProvider>
      <PortalScopeProvider>
        {/* Scope-aware title/favicon and colours: follow the scoped
            affiliate's home brand wherever they log in from. */}
        <PortalBrandingHead />
        <PortalBrandingTheme layer="scope" />
        <div className="relative flex min-h-screen bg-background">
          <button
            type="button"
            onClick={() => setIsMobileSidebarOpen(true)}
            aria-label="Open menu"
            className="fixed left-4 top-4 z-50 rounded-lg border border-border bg-card p-2 shadow-lg transition-colors hover:bg-accent md:hidden"
          >
            <IconMenu2 size={20} stroke={1.75} className="text-muted-foreground" />
          </button>

          <Sidebar
            isOpen={isMobileSidebarOpen}
            onClose={() => setIsMobileSidebarOpen(false)}
            onOpenDiscord={() => setShowDiscordModal(true)}
          />

          <InvitationNotifications />
          <JoinDiscordModal isOpen={showDiscordModal} onClose={handleCloseDiscordModal} />

          {/* min-w-0 + overflow-x-hidden: the page itself never scrolls
              sideways; wide tables scroll in their own container. pt-16 on
              mobile clears the fixed menu button. */}
          <main
            className={`min-w-0 flex-1 overflow-x-hidden ${
              isFullBleed ? "" : "px-4 pb-8 pt-16 sm:px-6 md:pt-8 lg:px-8"
            }`}
          >
            <OnboardingFloatingMenu isVisible={true} />
            {/* A scoped affiliate who has not joined their brand has nothing
                to see in here; the gate sends them to the join page. A no-op
                for everyone else. */}
            <ScopedMembershipGate>
              {isFullBleed ? (
                <Outlet />
              ) : (
                <div className="mx-auto w-full max-w-7xl">
                  <Outlet />
                  {/* White-label attribution, inside the gutter so it lines
                      up with page content. A no-op on the marketplace. */}
                  <PoweredByReveshare />
                </div>
              )}
            </ScopedMembershipGate>
          </main>
        </div>
      </PortalScopeProvider>
    </AffiliateProvider>
  );
}
